/**
 * Applications-list Malicious Score display helpers.
 *
 * eval-service returns Merlin's threat score (0-100) and its designation rule id on every row of
 * `eval/applications/app-list`. The designation — not the score — decides the label and colour, so the
 * column stays in step with whatever Merlin decided instead of re-deriving the Malicious Report's
 * thresholds here and drifting from it the next time they move.
 *
 * An app with no usable Merlin result reads as a grey "Unavailable" badge with no number. "No usable
 * result" means a missing/unrecognised designation, or a score that is absent or negative — eval writes
 * NULL when an analysis produced no Merlin result, and older analyses carry -1 as the same sentinel.
 * eval-service applies the identical rule when sorting and filtering by this column.
 */

/** QBadge colour variants; the palette is the Malicious Report badge's, not a new set of hex codes. */
export type MaliciousScoreVariant = 'green' | 'yellow' | 'red' | 'grey';

export type MaliciousScoreDisplay = {
  label: string;
  variant: MaliciousScoreVariant;
  /** null when there is no usable Merlin result, so the cell renders the badge on its own. */
  score: number | null;
};

/** eval-service stores the raw SARIF rule id, so the `malware/` prefix is part of the value. */
const DESIGNATION_DISPLAY: Record<string, { label: string; variant: MaliciousScoreVariant }> = {
  'malware/benign': { label: 'Benign', variant: 'green' },
  'malware/suspicious': { label: 'Suspicious', variant: 'yellow' },
  'malware/malicious': { label: 'Malicious', variant: 'red' },
};

const UNAVAILABLE: MaliciousScoreDisplay = { label: 'Unavailable', variant: 'grey', score: null };

/**
 * Resolves the label, badge colour and score shown in the Applications list's Malicious Score column.
 *
 * Nothing constrains the designation column, so a value that is not one of the three Merlin rule ids
 * falls through to Unavailable rather than rendering an uncoloured badge next to a score we cannot
 * explain.
 */
export const getMaliciousScoreDisplay = (
  merlinThreatScore: number | null | undefined,
  merlinDesignation: string | null | undefined,
): MaliciousScoreDisplay => {
  if (typeof merlinThreatScore !== 'number' || !Number.isFinite(merlinThreatScore) || merlinThreatScore < 0) {
    return UNAVAILABLE;
  }

  const designation = DESIGNATION_DISPLAY[merlinDesignation?.toLowerCase() ?? ''];

  return designation ? { ...designation, score: Math.round(merlinThreatScore) } : UNAVAILABLE;
};
